// src/pages/TermsPage.js
// Conditions d'utilisation CoryntiQ — modèle
// ⚠️ À FAIRE RELIRE PAR UN CONSEILLER JURIDIQUE AVANT LE LANCEMENT.
//
// À PERSONNALISER (cherche "À CONFIRMER") :
//   1. Raison sociale
//   2. Courriel de support
//   3. Date d'entrée en vigueur

import React from "react";
import { Shield, ArrowLeft } from "lucide-react";

const DERNIERE_MAJ = "17 juin 2026";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">{title}</h2>
    <div className="space-y-3 text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-[Inter]">
      {/* En-tête */}
      <header className="bg-[#1E3A5F] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0EA5E9]" />
            </div>
            <span className="text-lg font-bold">CoryntiQ</span>
          </a>
          <a
            href="/"
            className="flex items-center gap-1 text-sm text-sky-200 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </a>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">
          Conditions d'utilisation
        </h1>
        <p className="text-slate-500 mb-8">
          Dernière mise à jour : {DERNIERE_MAJ}
        </p>

        <Section title="1. Acceptation des conditions">
          <p>
            En créant un compte ou en utilisant CoryntiQ, vous acceptez les
            présentes conditions d'utilisation. Si vous n'y consentez pas,
            veuillez ne pas utiliser le service. CoryntiQ est exploité par{" "}
            <strong>CIGP Inc.</strong> (« CoryntiQ », « nous »).
          </p>
        </Section>

        <Section title="2. Description du service">
          <p>
            CoryntiQ est un logiciel d'aide à la gestion de la conformité à la
            Loi 25, destiné aux PME québécoises. Il fournit des outils,
            registres et modèles pour documenter et suivre vos démarches de
            conformité.
          </p>
        </Section>

        <Section title="3. Avis important — Outil, et non avis juridique">
          <p>
            CoryntiQ est un <strong>outil logiciel</strong>. Il ne constitue pas
            un avis juridique et ne remplace pas les conseils d'un avocat ou
            d'un professionnel qualifié. Bien que CoryntiQ vous aide à
            structurer votre conformité,{" "}
            <strong>nous ne garantissons pas</strong> que son utilisation rende
            votre organisation pleinement conforme à la Loi 25 ou à toute autre
            loi. La conformité demeure votre responsabilité et dépend de
            l'exactitude des informations que vous saisissez ainsi que de vos
            pratiques internes.
          </p>
        </Section>

        <Section title="4. Comptes">
          <p>
            Vous êtes responsable de l'exactitude des informations fournies, de
            la confidentialité de vos identifiants et de toute activité réalisée
            sous votre compte. Vous devez avoir l'autorité nécessaire pour lier
            votre organisation aux présentes conditions.
          </p>
        </Section>

        <Section title="5. Essai gratuit, abonnements et facturation">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Essai gratuit</strong> : 14 jours. Une carte valide est
              requise, mais aucun montant n'est débité pendant l'essai.
            </li>
            <li>
              <strong>Plans</strong> : Essentiel (199 $/mois ou 1 990 $/an) et
              Professionnel (449 $/mois ou 4 490 $/an), en dollars canadiens
              (CAD), taxes en sus.
            </li>
            <li>
              <strong>Renouvellement</strong> : à la fin de l'essai, puis à
              chaque période, l'abonnement se renouvelle automatiquement au
              tarif en vigueur, sauf annulation.
            </li>
            <li>
              <strong>Annulation</strong> : vous pouvez annuler en tout temps.
              L'annulation prend effet à la fin de la période déjà payée; le
              service reste accessible jusque-là.
            </li>
            <li>
              <strong>Paiements</strong> : traités de façon sécurisée par
              Moneris. Nous ne stockons pas votre numéro de carte complet.
            </li>
          </ul>
        </Section>

        <Section title="6. Utilisation acceptable">
          <p>Vous vous engagez à ne pas :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>utiliser le service à des fins illégales;</li>
            <li>
              tenter d'accéder sans autorisation à nos systèmes ou à ceux
              d'autrui;
            </li>
            <li>
              nuire au fonctionnement du service ou contourner ses mesures de
              sécurité;
            </li>
            <li>
              téléverser du contenu pour lequel vous n'avez pas les droits
              requis.
            </li>
          </ul>
        </Section>

        <Section title="7. Propriété intellectuelle">
          <p>
            Le logiciel CoryntiQ, sa marque, son design et son contenu demeurent
            la propriété de CIGP Inc. Les données que vous saisissez vous
            appartiennent; vous nous accordez le droit limité de les traiter aux
            seules fins de vous fournir le service.
          </p>
        </Section>

        <Section title="8. Disponibilité et limitation de responsabilité">
          <p>
            Nous nous efforçons d'assurer un service fiable, mais celui-ci est
            fourni « tel quel », sans garantie de disponibilité ininterrompue.
            Dans la mesure permise par la loi, notre responsabilité totale
            envers vous est limitée au montant que vous avez payé pour le
            service au cours des douze (12) derniers mois.
          </p>
        </Section>

        <Section title="9. Résiliation">
          <p>
            Vous pouvez fermer votre compte en tout temps. Nous pouvons
            suspendre ou résilier l'accès en cas de manquement aux présentes
            conditions. À la résiliation, vous pouvez demander l'exportation ou
            la suppression de vos données, sous réserve de nos obligations
            légales.
          </p>
        </Section>

        <Section title="10. Modifications">
          <p>
            Nous pouvons modifier ces conditions. En cas de changement
            important, nous vous en informerons. La poursuite de l'utilisation
            du service après l'entrée en vigueur des modifications vaut
            acceptation.
          </p>
        </Section>

        <Section title="11. Droit applicable">
          <p>
            Les présentes conditions sont régies par les lois de la province de
            Québec et les lois fédérales du Canada qui y sont applicables. Tout
            litige sera soumis aux tribunaux compétents du Québec.
          </p>
        </Section>

        <Section title="12. Nous joindre">
          <p>
            Pour toute question : support@coryntiq.ca {/* À CONFIRMER */} — CIGP
            Inc., Québec, Canada.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <a href="/privacy" className="text-[#0EA5E9] hover:underline">
            Politique de confidentialité
          </a>{" "}
          · © {new Date().getFullYear()} CIGP Inc. — CoryntiQ
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
